import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useTeamAliases } from "@/hooks/useTeamAliases";
import { computeUnreadFeedIds, unreadFeedIdsCollections } from "@/models/repositories";
import type { Item } from "@/store/types";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { handleFaviconChange, ipcRendererSend } from "@/utils";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";

type UnreadFeed = {
  feedId: string;
  unreadItems: Array<Item>;
};

export { unreadClearEvents } from "@/models/actions/readEvents";

type UnreadsState = {
  allUnreadFeeds?: Array<UnreadFeed>;
  unreadFeedIds?: Array<string>;
  /**
   * Hands the badge (favicon, title count, desktop count) a page's own ids to count in place of the unread channels;
   * `null` gives it back to unread. The Dispatch view counts its rows showing the red dot here.
   */
  setBadgeIds?: (ids: readonly string[] | null) => void;
};

export const UnreadsContext = createContext<UnreadsState>({
  allUnreadFeeds: [],
});

const UnreadsContextProvider = ({ children }) => {
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { teamAliases } = useTeamAliases(workspaceId ?? "", myCurrentWorkspaceMembership?.id ?? "");
  const [badgeOverride, setBadgeIds] = useState<readonly string[] | null>(null);

  const myMembershipId = myCurrentWorkspaceMembership?.id;
  const teamAliasesKey = teamAliases.join(",");
  const unreadFeedIds = useCollectionsCompute(
    unreadFeedIdsCollections,
    () =>
      myMembershipId
        ? computeUnreadFeedIds({
            myCurrentWorkspaceMembershipId: myMembershipId,
            teamAliases: teamAliases.length > 0 ? teamAliases : undefined,
            requireNotificationPreference: true,
          })
        : [],
    [myMembershipId, teamAliasesKey],
    "UnreadsContextProvider:unreadFeedIds",
  );

  const mappedFeedIds = useMemo(() => unreadFeedIds?.map((ufi) => ufi.feedId) ?? [], [unreadFeedIds]);

  useEffect(() => {
    // A page's override wins while one stands, an empty one included; otherwise the badge counts unread channels.
    const counted = badgeOverride ?? mappedFeedIds;
    handleFaviconChange(counted);
    ipcRendererSend("update-count", counted.length);
  }, [mappedFeedIds, badgeOverride]);

  const unreadsState: UnreadsState = {
    unreadFeedIds: mappedFeedIds,
    setBadgeIds,
  };

  return <UnreadsContext.Provider value={unreadsState}>{children}</UnreadsContext.Provider>;
};

export default UnreadsContextProvider;
